import axiosWithoutHandler from "../utils/axiosWithoutHandler";
import appErrorToast from "../components/common/AppErrorToast";

export type DownloadExcelBlobOptions = {
  url: string;
  fallbackDownloadName: string;
  setIsLoading: (value: boolean) => void;
  genericErrorMessage: string;
};

function parseFilenameFromContentDisposition(
  header: string | undefined,
  fallback: string
): string {
  if (!header) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch { }
  }
  const asciiMatch = /filename=["']?([^"';\n]+)["']?/i.exec(header);
  if (asciiMatch?.[1]) return asciiMatch[1].trim();
  return fallback;
}

function triggerBrowserFileDownload(blob: Blob, downloadName: string): void {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", downloadName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

/** GET as blob. On failure or JSON body (API error), shows only genericErrorMessage. */
export async function downloadExcelBlobFromUrl(options: DownloadExcelBlobOptions): Promise<void> {
  const { url, fallbackDownloadName, setIsLoading, genericErrorMessage } = options;
  setIsLoading(true);
  try {
    const response = await axiosWithoutHandler.get<Blob>(url, { responseType: "blob" });
    const contentType = String(response.headers["content-type"] ?? "");
    if (contentType.includes("application/json")) {
      appErrorToast(genericErrorMessage);
      return;
    }
    const filename = parseFilenameFromContentDisposition(
      response.headers["content-disposition"],
      fallbackDownloadName
    );
    triggerBrowserFileDownload(response.data, filename);
  } catch {
    appErrorToast(genericErrorMessage);
  } finally {
    setIsLoading(false);
  }
}
